/**
 * Who may do what (docs/13 §5.1-5.3). Pure, so the rules are testable without a screen.
 *
 * `song.locked` is deliberately absent: it is always false in this phase. Access comes from
 * `song.access` on `/web/*` responses, or from the free list for a plain `SongDTO` (§5.2).
 */
import type { PlanSummary, SongAccess, SongDTO, WebSongDTO } from '@akpl/shared';

export type ViewerTier = 'visitor' | 'free' | 'premium';

export function viewerTier(isAccount: boolean, plan: PlanSummary | undefined): ViewerTier {
  if (!isAccount) return 'visitor';
  return plan?.tier === 'premium' ? 'premium' : 'free';
}

/** A song from `/web/*` carries its access; a plain one (`/search`) is compared with the list. */
export function songAccess(
  song: SongDTO | WebSongDTO,
  freeIds: ReadonlySet<string> | undefined,
): SongAccess {
  if ('access' in song) return song.access;
  return freeIds?.has(song.id) ? 'free' : 'premium';
}

/**
 * - `signup`: crear cuenta, luego el reproductor
 * - `signup-paywall`: crear cuenta, luego el paywall
 * - `paywall`: W-05
 * - `play`: reproductor
 */
export type SingOutcome = 'signup' | 'signup-paywall' | 'paywall' | 'play';

export function singOutcome(tier: ViewerTier, access: SongAccess): SingOutcome {
  if (tier === 'visitor') return access === 'free' ? 'signup' : 'signup-paywall';
  if (tier === 'premium' || access === 'free') return 'play';
  return 'paywall';
}

/** With Premium active there are no PREMIUM badges (§5.2). */
export function visibleBadge(tier: ViewerTier, access: SongAccess): SongAccess | null {
  if (access === 'free') return 'free';
  return tier === 'premium' ? null : 'premium';
}

export type AccountScreen = 'free' | 'active' | 'trialing' | 'canceling' | 'pastDue';

/** §5.2: which "Mi cuenta" variant a plan maps to (W-09, E-18, E-19, E-20, E-21). */
export function accountScreen(plan: PlanSummary | undefined): AccountScreen {
  if (!plan || plan.tier === 'free') return 'free';
  if (plan.status === 'past_due') return 'pastDue';
  if (plan.status === 'trialing') return 'trialing';
  return plan.cancelAtPeriodEnd ? 'canceling' : 'active';
}
